/**
 * ============================================================================
 *                    SIDEBAR STYLES & ICON COLORS
 *                    Developer Guide & Configuration
 * ============================================================================
 * 
 * This file controls the visual appearance of the sidebar navigation.
 * 
 * QUICK START - Adding a new icon color:
 * ----------------------------------------
 * 1. Add your color to `iconColors` object below
 * 2. Add a matching rule in `getIconColor` function
 * 
 * Example: Adding "analytics" category with purple color:
 *   Step 1: iconColors.analytics = '#9C27B0'
 *   Step 2: if (nameLower.includes('analytics')) return iconColors.analytics;
 * 
 * ============================================================================
 */

// ============================================================================
// THEME CONFIGURATION
// ============================================================================
export const sidebarTheme = {
    // -- BACKGROUND COLORS --
    backgroundColor: '#3a3633',        // Main sidebar background (dark brown-gray)
    paperBg: '#454240',                // Nested/popup background (slightly lighter)

    // -- TEXT COLORS --
    textColor: '#d4d4d4',              // Primary text (light gray)
    textColorMuted: '#8a8a8a',         // Secondary text, icons (muted gray)

    // -- INTERACTIVE STATES --
    hoverBg: 'rgba(255,255,255,0.04)', // Hover effect (subtle white overlay)
    activeBg: '#F4F5FA',               // Selected item background (light)
    activeText: '#2B3467',             // Selected item text color (dark blue)

    // -- VISUAL SEPARATORS --
    submenuBorderColor: 'rgba(255,255,255,0.1)', // Left border for submenus

    // -- DIMENSIONS --
    drawerWidth: 240,                  // Expanded sidebar width (pixels)
    drawerWidthMinified: 56,           // Collapsed sidebar width (pixels)
    headerHeight: 50,                  // Top header bar height (pixels)

    // -- ANIMATION --
    transition: 'all 0.15s ease',      // Smooth transitions
};

// ============================================================================
// CUSTOM SCROLLBAR (for dark theme)
// ============================================================================
export const scrollbarStyles = {
    "&::-webkit-scrollbar": {
        width: 5,
    },
    "&::-webkit-scrollbar-track": {
        background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "rgba(255,255,255,0.2)",
        borderRadius: 3,
        "&:hover": {
            background: "rgba(255,255,255,0.3)",
        },
    },
    // Firefox support
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.2) transparent",
};

// ============================================================================
// ICON COLORS BY CATEGORY
// ============================================================================
// 
// HOW TO ADD A NEW COLOR:
// Simply add a new key-value pair below. Use any valid CSS color.
// 
// Color naming tips:
//   - Use descriptive names matching your menu categories
//   - Pick colors from Material Design palette: https://materialui.co/colors
// 
export const iconColors = {
    // Core navigation
    dashboard: '#4CAF50',      // Green
    favourite: '#E91E63',      // Pink

    // Content management
    contract: '#FF9800',       // Orange
    media: '#2196F3',          // Blue
    mam: '#9C27B0',            // Purple
    content: '#00BCD4',        // Cyan
    vod: '#3F51B5',            // Indigo

    // Scheduling & Planning
    planning: '#FFC107',       // Amber
    scheduling: '#673AB7',     // Deep Purple

    // Publishing & Distribution
    publish: '#009688',        // Teal
    timeline: '#607D8B',       // Blue Gray
    ingestion: '#8BC34A',      // Light Green

    // Business
    sales: '#FF5722',          // Deep Orange
    social: '#03A9F4',         // Light Blue
    reconciliation: '#CDDC39', // Lime
    secondary: '#FFEB3B',      // Yellow
    billing: '#E91E63',        // Pink

    // Advanced features
    curate: '#00BCD4',         // Cyan
    dai: '#FF7043',            // Deep Orange (light)
    monetization: '#26A69A',   // Teal (light)
    bucket: '#5C6BC0',         // Indigo (light)

    // System
    notification: '#EF5350',   // Red
    service: '#78909C',        // Blue Gray (light)
    export: '#66BB6A',         // Green (light)
    masters: '#AB47BC',        // Purple (light)
    report: '#42A5F5',         // Blue (light)
    users: '#EC407A',          // Pink (light)
    settings: '#BDBDBD',       // Gray

    // Fallback
    default: '#90A4AE',        // Blue Gray (very light) - used when no match found
};

// ============================================================================
// GET ICON COLOR BY MODULE NAME
// ============================================================================
// 
// This function automatically assigns colors based on menu item names.
// It searches for keywords in the menu item name (case-insensitive).
// 
// HOW TO ADD A NEW COLOR RULE:
// 1. First add the color to `iconColors` above
// 2. Then add a matching condition below
// 
// Example - Adding "analytics" category:
//   if (nameLower.includes('analytics')) return iconColors.analytics;
// 
// TIP: You can match multiple keywords with OR:
//   if (nameLower.includes('chart') || nameLower.includes('graph')) return iconColors.report;
// 
export const getIconColor = (moduleName) => {
    const nameLower = (moduleName || '').toLowerCase();

    // Match by keyword (order matters - first match wins)
    if (nameLower.includes('dashboard')) return iconColors.dashboard;
    if (nameLower.includes('favourite') || nameLower.includes('favorite')) return iconColors.favourite;
    if (nameLower.includes('contract')) return iconColors.contract;
    if (nameLower.includes('media') || nameLower.includes('mam')) return iconColors.media;
    if (nameLower.includes('vod')) return iconColors.vod;
    if (nameLower.includes('planning')) return iconColors.planning;
    if (nameLower.includes('scheduling')) return iconColors.scheduling;
    if (nameLower.includes('publish')) return iconColors.publish;
    if (nameLower.includes('timeline')) return iconColors.timeline;
    if (nameLower.includes('ingestion')) return iconColors.ingestion;
    if (nameLower.includes('sales')) return iconColors.sales;
    if (nameLower.includes('social')) return iconColors.social;
    if (nameLower.includes('reconcil')) return iconColors.reconciliation;
    if (nameLower.includes('billing')) return iconColors.billing;
    if (nameLower.includes('curate')) return iconColors.curate;
    if (nameLower.includes('dai') || nameLower.includes('dynamic')) return iconColors.dai;
    if (nameLower.includes('monetiz')) return iconColors.monetization;
    if (nameLower.includes('bucket')) return iconColors.bucket;
    if (nameLower.includes('notification')) return iconColors.notification;
    if (nameLower.includes('service')) return iconColors.service;
    if (nameLower.includes('export')) return iconColors.export;
    if (nameLower.includes('master')) return iconColors.masters;
    if (nameLower.includes('report')) return iconColors.report;
    if (nameLower.includes('user')) return iconColors.users;
    if (nameLower.includes('setting') || nameLower.includes('config')) return iconColors.settings;

    // Fallback for unmatched items
    return iconColors.default;
};
